import Phaser from "phaser";
import { shouldRestartOnQuit } from "../flow/entry";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  applyFrontlineBoardMetrics,
  boardLayoutChanged,
  computeFrontlineBoardLayout,
  createFrontlineState,
  drainEvents,
  getHudSnapshot,
  tickFrontline,
  selectHandIndex,
  tryCastSkill,
  worldToCell,
  cellCenter,
  canDeployAt,
  cardCellRange,
  unitsInCellRange,
  FlEvent,
  type FrontlineState,
  type FlSkillId,
} from "../features/frontline";
import { frontlineCatalog } from "../data/frontline-catalog";
import { FrontlinePresentation } from "../features/frontline/view/frontline-presentation";
import { cssPxToGamePx } from "../features/portal-ffa/view/camera-gesture";
import { sharedInput } from "../input/input-controller";
import { mountFrontlineHud } from "../ui/huds/frontline";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";
import { t } from "../data/strings";

const SKILLS = frontlineCatalog.skills.cards as Record<
  string,
  { target?: string; radius?: number; cellRange?: number } | undefined
>;

function cardRadius(skill: FlSkillId): number {
  return SKILLS[skill]?.radius ?? 48;
}

export class FrontlineScene extends Phaser.Scene {
  private state!: FrontlineState;
  private view!: FrontlinePresentation;
  private hud!: ReturnType<typeof mountFrontlineHud>;
  private disposed = false;
  private toast = "";
  private layoutRefreshQueued = false;

  constructor() {
    super(SceneKey.Frontline);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.Frontline);
    sharedInput.attach();
    clearOverlay();
    getUiLayout().sync();

    this.cameras.main.setBackgroundColor("#101820");
    this.state = createFrontlineState();
    this.view = new FrontlinePresentation(this);
    this.view.create(this.state);

    this.hud = mountFrontlineHud({
      onSelectHandIndex: (index: number) => {
        selectHandIndex(this.state, index);
        this.toast = this.state.selectedSkill
          ? t(`fl.msg.skill.${this.state.selectedSkill}`)
          : t("fl.msg.deselected");
      },
      onQuit: () => this.quitToTitle(),
      onRestart: () => this.restartMatch(),
    });
    this.hud.sync(getHudSnapshot(this.state));
    // After first paint, measure real chrome and fit the board.
    this.time.delayedCall(0, () => this.refreshBoardLayout(true));

    this.scale.on("resize", this.onScaleResize, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.input.on("pointerdown", this.onPointerDown, this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      this.quitToTitle();
      return;
    }
    const dt = Math.min(0.05, dtMs / 1000);
    tickFrontline(this.state, dt);
    const events = drainEvents(this.state);
    for (const ev of events) {
      if (ev.kind === FlEvent.Skill) {
        this.hud.pushCastNoti({ team: ev.team, skillId: ev.skillId });
      }
    }
    this.view.sync(this.state, events);
    this.hud.sync(getHudSnapshot(this.state), this.toast);
  }

  private onScaleResize = (): void => {
    if (this.disposed) return;
    if (this.layoutRefreshQueued) return;
    this.layoutRefreshQueued = true;
    this.time.delayedCall(0, () => {
      this.layoutRefreshQueued = false;
      this.refreshBoardLayout(false);
    });
  };

  private resolveHudInsetsGamePx(): { top: number; bottom: number } {
    const bal = frontlineCatalog.balance;
    const fallbackTop = bal.originY;
    const fallbackBottom = Math.max(
      120,
      this.scale.height - (bal.originY + bal.rows * bal.cellH),
    );
    const canvas = this.game.canvas;
    if (!canvas) return { top: fallbackTop, bottom: fallbackBottom };
    const rect = canvas.getBoundingClientRect();
    const topEl = document.querySelector(".fl-top") as HTMLElement | null;
    const dockEl = document.querySelector(".fl-dock") as HTMLElement | null;
    const topCss = topEl?.getBoundingClientRect().height ?? 0;
    const bottomCss = dockEl?.getBoundingClientRect().height ?? 0;
    if (topCss <= 0 && bottomCss <= 0) {
      return { top: fallbackTop, bottom: fallbackBottom };
    }
    return {
      top: cssPxToGamePx(
        Math.max(topCss, 1),
        rect.height,
        this.scale.height,
      ),
      bottom: cssPxToGamePx(
        Math.max(bottomCss, 1),
        rect.height,
        this.scale.height,
      ),
    };
  }

  private refreshBoardLayout(force: boolean): void {
    if (this.disposed || !this.state || !this.view) return;
    getUiLayout().sync();
    const insets = this.resolveHudInsetsGamePx();
    const bal = frontlineCatalog.balance;
    const fallback = {
      originX: this.state.originX,
      originY: this.state.originY,
      cellW: this.state.cellW,
      cellH: this.state.cellH,
    };
    const next = computeFrontlineBoardLayout({
      gameW: this.scale.width,
      gameH: this.scale.height,
      topInset: insets.top,
      bottomInset: insets.bottom,
      cols: bal.cols,
      rows: bal.rows,
      fallback: {
        originX: bal.originX,
        originY: bal.originY,
        cellW: bal.cellW,
        cellH: bal.cellH,
      },
    });
    if (!force && !boardLayoutChanged(fallback, next)) return;
    applyFrontlineBoardMetrics(this.state, next);
    this.view.relayoutBoard(this.state);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.disposed || this.state.phase !== "playing") return;
    const skill = this.state.selectedSkill;
    if (!skill) {
      this.view.clearCastPreview();
      return;
    }
    const def = SKILLS[skill];
    const target = def?.target ?? "cell";
    const x = pointer.worldX;
    const y = pointer.worldY;

    if (target === "board") {
      this.view.setPreview(false, 0, 0, false);
      this.view.setAoePreview(false, 0, 0, 0, false);
      this.view.clearAoeCellFootprint();
      this.view.setOwnedDeployHints(this.state, 0, false);
      this.view.setAimHighlights([]);
      this.view.setBoardWarn(true);
      return;
    }

    this.view.setBoardWarn(false);

    if (target === "cell") {
      this.view.setAoePreview(false, 0, 0, 0, false);
      this.view.clearAoeCellFootprint();
      this.view.setAimHighlights([]);
      this.view.setOwnedDeployHints(this.state, 0, true);
      const cell = worldToCell(this.state, x, y);
      if (!cell) {
        this.view.setPreview(false, 0, 0, false);
        return;
      }
      const ok = canDeployAt(this.state, 0, cell.col, cell.row);
      const c = cellCenter(this.state, cell.col, cell.row);
      this.view.setPreview(true, c.x, c.y, ok);
      if (skill === "legion" || skill === "horde") {
        const rad = cardRadius(skill);
        this.view.setAoePreview(true, c.x, c.y, rad * 0.55, ok);
      }
      return;
    }

    // AoE cards — Chebyshev cell footprint
    this.view.setPreview(false, 0, 0, false);
    this.view.setAoePreview(false, 0, 0, 0, false);
    this.view.setOwnedDeployHints(this.state, 0, false);
    const cell = worldToCell(this.state, x, y);
    if (!cell) {
      this.view.clearAoeCellFootprint();
      this.view.setAimHighlights([]);
      return;
    }
    const range = cardCellRange(skill);
    const foeAim = skill === "smite" || skill === "stun";
    const ids = unitsInCellRange(
      this.state,
      foeAim ? "enemy" : "ally",
      0,
      cell.col,
      cell.row,
      range,
    ).map((u) => u.id);
    const ok = ids.length > 0;
    this.view.setAoeCellFootprint(
      this.state,
      cell.col,
      cell.row,
      range,
      ok,
    );
    this.view.setAimHighlights(ids);
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.disposed || this.state.phase !== "playing") return;
    if (!this.state.selectedSkill) return;
    const r = tryCastSkill(this.state, 0, pointer.worldX, pointer.worldY);
    if (!r.ok) {
      this.toast = t(`fl.deploy.${r.reason}`);
      return;
    }
    this.view.clearCastPreview();
    this.toast = t("fl.msg.hint");
  }

  private restartMatch(): void {
    this.teardown();
    this.scene.restart();
  }

  private quitToTitle(): void {
    if (this.disposed) return;
    if (shouldRestartOnQuit(SceneKey.Frontline)) {
      assertTransition(SceneKey.Frontline, SceneKey.Frontline);
      this.teardown();
      this.scene.start(SceneKey.Frontline);
      return;
    }
    assertTransition(SceneKey.Frontline, SceneKey.Title);
    this.teardown();
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scale.off("resize", this.onScaleResize, this);
    this.input.off("pointermove", this.onPointerMove, this);
    this.input.off("pointerdown", this.onPointerDown, this);
    try {
      this.view?.destroy();
    } catch (err) {
      console.error("[frontline] view destroy", err);
    }
    try {
      this.hud?.destroy();
    } catch (err) {
      console.error("[frontline] hud destroy", err);
    }
    clearOverlay();
  }
}
